import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../components/AuthProvider';
import { showTemporaryMessage } from "../components/Message";
import logoicon from '../images/tuutuum-logo-icon.png';
import { LayoutContext } from '../components/LayoutProvider';

import PickUser from '../components/PickUser';
import ListOfBearers from '../components/ListOfBearers';
import CreateBearer from '../components/CreateBearer';

import { update_scan_token } from "../tools-network";
import NeumorphicContainer from './NeumorphicContainer';

const RegisterQrCodeStep2 = ({ onNext, onPrevious, reportSelectedBearerId }) => {
    const { t } = useTranslation();
    const { userType, loginToken, userId, handleInvalidToken } = React.useContext(AuthContext);

    const [currentSubStep, setCurrentSubStep] = useState('selectBearer'); // 'selectBearer' or 'createBearer'
    const { setIsHeaderVisible } = React.useContext(LayoutContext);

    const [selectedUserId, setSelectedUserId] = useState(0);
    const [selectedBearerId, setSelectedBearerId] = useState(0);
    const [selectedBearerName, setSelectedBearerName] = useState('');
    const [refreshBearerListTrigger, setRefreshBearerListTrigger] = useState(0);
    const [noBearersAvailable, signalNoBearersAvailable] = useState(false);
    const [bearerListHasLoaded, setBearerListHasLoaded] = useState(false);

    const queryParams = new URLSearchParams(location.search);
    const pending_token = queryParams.get('pending_token');

    useEffect(() => {
        // Only auto-forward after the bearer list has loaded at least once
        if (bearerListHasLoaded && noBearersAvailable && currentSubStep === 'selectBearer') {
            setCurrentSubStep('createBearer');
        }
    }, [bearerListHasLoaded, noBearersAvailable, currentSubStep]);

    useEffect(() => {
        setIsHeaderVisible(false);

        return () => setIsHeaderVisible(true);
    }, [setIsHeaderVisible]);

    useEffect(() => {
        if (userType === 'REGULAR') {
            setSelectedUserId(userId);
        }
    }, [userType, userId]);

    const handlePrevious = async () => {
        console.log("Handle previous!");
        onPrevious();
    };

    const handleNext = async () => {
        console.log("Handle Next!");
        await setBearerId();
        reportSelectedBearerId(selectedBearerId);
        onNext(selectedBearerName);
    };

    const createBearer = () => {
        console.log("CREATE BEARER!");
    };

    const setBearerId = async () => {
        if (selectedBearerId > 0) {
            try {
                let updateFields = { bearer_id: selectedBearerId };
                let res = await update_scan_token(loginToken, pending_token, updateFields);
                if (res.success) {
                    showTemporaryMessage(t("scan_token_bearer_id_successfully_updated"), " ", 'success');
                } else {
                    console.log("UPDATE SCAN TOKEN FAILED: " + JSON.stringify(res));
                    return;
                }
            } catch (err) {
                console.log("Error updating scan code: " + err);
                showTemporaryMessage("Scan token information failed to update!", " ", 'fail');
                return;
            }
        }
    };

    const handleCreateBearer = () => {
        setCurrentSubStep('createBearer');
    };

    const handleBearerCreated = async (bearerId, bearerName) => {
        // Set the bearer ID and update the scan token
        setSelectedBearerId(bearerId);
        reportSelectedBearerId(bearerId);
        
        if (bearerId > 0) {
            try {
                let updateFields = { bearer_id: bearerId };
                let res = await update_scan_token(loginToken, pending_token, updateFields);
                if (res.success) {
                    showTemporaryMessage(t("scan_token_bearer_id_successfully_updated"), " ", 'success');
                    // Auto-advance to next step with bearer name for wristband suggestion
                    onNext(bearerName);
                } else {
                    console.log("UPDATE SCAN TOKEN FAILED: " + JSON.stringify(res));
                    setCurrentSubStep('selectBearer');
                }
            } catch (err) {
                console.log("Error updating scan code: " + err);
                showTemporaryMessage("Scan token information failed to update!", " ", 'fail');
                setCurrentSubStep('selectBearer');
            }
        }
    };

    return (
        <>
            {currentSubStep === 'selectBearer' && (
                <NeumorphicContainer>
                    <h2 className="text-center text-2xl leading-tight px-2 my-4">
                        {t("who_will_wear_this_wristband")}
                    </h2>
                    <p className="font-semibold">
                        {noBearersAvailable ? t("click_create_new_bearer_to_add_bearer") : t("select_person_in_list_or_create_new_one")}
                    </p>
                    {userType === 'ADMIN' && (
                        <div className="w-full">
                            <PickUser
                                loginToken={loginToken}
                                setSelectedUserId={setSelectedUserId}
                                filter={(user) => user.type === 'REGULAR'}
                                refreshTrigger={null}
                            />
                        </div>
                    )}
                    <div className="w-full">
                        <ListOfBearers
                            loginToken={loginToken}
                            userId={userId}
                            setSelectedBearerId={setSelectedBearerId}
                            reportBearerName={setSelectedBearerName}
                            signalNoBearersAvailable={signalNoBearersAvailable}
                            signalBearerListLoaded={setBearerListHasLoaded}
                            refreshBearerListTrigger={refreshBearerListTrigger}
                        />
                    </div>
                    <button
                        onClick={handleCreateBearer}
                        className="create-bearer-enable btn-secondary btn-secondary-hover w-full font-semibold py-2 mx-4 rounded-md text-center text-sm disabled:bg-gray-400 disabled:cursor-not-allowed transition ease-in-out hover:-translate-y-0.5 hover:scale-105 hover:bg-cyan-600 hover:text-white hover:outline-none"
                    >
                        {t("create_bearer")}
                    </button>
                    <div className="flex flex-nowrap w-full gap-4 justify-between">
                        <button
                            className="btn-secondary btn-secondary:hover text-white px-3 py-1 w-auto rounded-md sm:leading-6 text-center font-semibold text-sm transition ease-in-out hover:scale-105"
                            onClick={handlePrevious}
                        >
                            {t("previous")}
                        </button>
                        <button
                            className="btn-main btn-main:hover text-white px-3 py-1 w-auto rounded-md sm:leading-6 text-center font-semibold text-sm transition ease-in-out hover:scale-105 disabled:bg-gray-400 disabled:text-grey-600 disabled:cursor-not-allowed"
                            disabled={selectedBearerId < 1}
                            onClick={handleNext}
                        >
                            {t("next")}
                        </button>
                    </div>
                </NeumorphicContainer>
            )}
            {currentSubStep === 'createBearer' && (
                <CreateBearer
                    onBearerCreated={handleBearerCreated}
                    onCancel={noBearersAvailable ? null : () => setCurrentSubStep('selectBearer')}
                />
            )}
        </>
    );
}

export default RegisterQrCodeStep2;